import { useState } from "react";
import { Linkedin, Twitter, MessageCircle, Mail, Phone, ChevronDown } from "lucide-react";
import { trackOutboundSocialClick, trackEmailClick } from "@/lib/analytics";
import { useLocation } from "wouter";

const LOGO_URL = "https://integrated.social/media/integrated-social-logo_c362b0f0.webp";
const COMPACT_LOGO_URL = "/media/logo-32_869c351c.webp";
const COMPACT_LOGO_URL_2X = "/media/logo-64_95919a69.webp";

// Accordion column for mobile
function FooterAccordion({ title, titleColor, children }: { title: string; titleColor: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 md:border-none">
      {/* Mobile: clickable header */}
      <button
        className="w-full flex items-center justify-between py-4 md:hidden"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-mono text-xs uppercase tracking-widest" style={{ color: titleColor }}>{title}</span>
        <ChevronDown
          size={16}
          className="text-white/40 transition-transform duration-200"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      {/* Desktop: always visible heading */}
      <h3 className="hidden md:block font-mono text-xs uppercase tracking-widest mb-4" style={{ color: titleColor }}>{title}</h3>
      {/* Content: hidden on mobile unless open */}
      <div className={`overflow-hidden transition-all duration-200 ${open ? "max-h-[600px] pb-4" : "max-h-0 md:max-h-none"} md:block`}>
        {children}
      </div>
    </div>
  );
}

export default function Footer({ profileRestrictedServices = false }: { profileRestrictedServices?: boolean }) {
  const [location] = useLocation();
  const path = location.split(/[?#]/)[0].replace(/\/?$/, "/");
  const preserveFrozenLogo = path.startsWith("/local/") || [
    "/services/ai-visibility-plan/",
    "/services/local-presence-plan/",
    "/services/full-ai-growth-engine/",
  ].includes(path);
  return (
    <footer className="bg-[#0F1419] py-10 md:py-16 lg:py-20" role="contentinfo">
      <div className="container">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-0 md:gap-10 lg:gap-8 mb-8 md:mb-12">
          {/* Brand */}
          <div className="lg:col-span-2 pb-6 md:pb-0 border-b border-white/10 md:border-none">
            <a href="/" className="flex items-center gap-3 mb-4">
              <img
                src={preserveFrozenLogo ? LOGO_URL : COMPACT_LOGO_URL}
                srcSet={preserveFrozenLogo ? undefined : `${COMPACT_LOGO_URL} 1x, ${COMPACT_LOGO_URL_2X} 2x`}
                alt="Integrated.Social"
                className="w-8 h-8"
                width={32}
                height={32}
                loading={preserveFrozenLogo ? undefined : "lazy"}
                decoding={preserveFrozenLogo ? undefined : "async"}
              />
              <span className="text-white font-serif text-lg">
                Integrated<span className="text-[#FF8C00] font-sans font-bold">.Social</span>
              </span>
            </a>
            <p className="text-white/50 text-sm leading-relaxed max-w-md mb-3">
              We help you turn AI visibility into enquiries. We do not promise results.
            </p>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mb-6">
              <a href="/services/seo-aeo-geo/" className="text-[#00D4AA]/60 hover:text-[#00D4AA] text-xs transition-colors">AEO</a>
              <span className="text-white/20 text-xs">·</span>
              <a href="/services/seo-aeo-geo/" className="text-[#00D4AA]/60 hover:text-[#00D4AA] text-xs transition-colors">GEO</a>
              <span className="text-white/20 text-xs">·</span>
              <a href="/services/seo-aeo-geo/" className="text-[#00D4AA]/60 hover:text-[#00D4AA] text-xs transition-colors">LLMO</a>
              <span className="text-white/20 text-xs">·</span>
              <a href="/services/gemini-agentic-ai/" className="text-[#FF8C00]/60 hover:text-[#FF8C00] text-xs transition-colors">Agentic AI</a>
              <span className="text-white/20 text-xs">·</span>
              <a href="/services/ppc-performance-max/" className="text-[#A78BFA]/60 hover:text-[#A78BFA] text-xs transition-colors">Performance Max</a>
              <span className="text-white/20 text-xs">·</span>
              <a href="/services/google-ai-mode-optimisation/" className="text-[#00D4AA]/60 hover:text-[#00D4AA] text-xs transition-colors">Google AI Mode</a>
            </div>
            <div className="flex gap-4">
              <a
                href="https://www.linkedin.com/company/integrated-social-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#FF8C00] transition-colors"
                aria-label="LinkedIn"
                onClick={() => trackOutboundSocialClick({ social_platform: "linkedin", link_location: "footer", destination_url: "https://www.linkedin.com/company/integrated-social-ltd" })}
              >
                <Linkedin size={18} />
              </a>
              <a
                href="https://x.com/MoElnadi"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#FF8C00] transition-colors"
                aria-label="X (Twitter)"
                onClick={() => trackOutboundSocialClick({ social_platform: "twitter", link_location: "footer", destination_url: "https://x.com/MoElnadi" })}
              >
                <Twitter size={18} />
              </a>
              <a
                href="https://wa.me/447717804077"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#25D366] transition-colors"
                aria-label="WhatsApp"
                onClick={() => trackOutboundSocialClick({ social_platform: "whatsapp", link_location: "footer", destination_url: "https://wa.me/447717804077" })}
              >
                <MessageCircle size={18} />
              </a>
            </div>
          </div>

          {/* Services */}
          <FooterAccordion title="AI Growth Services" titleColor="#FF8C00">
            <ul className="space-y-2">
              <li><a href="/services/gemini-agentic-ai/" className="text-[#FF8C00]/80 hover:text-[#FF8C00] text-sm transition-colors font-medium">Agentic AI Lead Gen</a></li>
              <li><a href="/services/seo-aeo-geo/" className="text-[#00D4AA]/80 hover:text-[#00D4AA] text-sm transition-colors font-medium">AEO · GEO · LLMO · AI Search</a></li>
              <li><a href="/services/ppc-performance-max/" className="text-[#A78BFA]/80 hover:text-[#A78BFA] text-sm transition-colors font-medium">PPC &amp; Performance Max</a></li>
              <li className="pt-1 border-t border-white/10 mt-1"></li>
              <li><a href="/services/google-ai-mode-optimisation/" className="text-white/50 hover:text-white text-sm transition-colors">Google AI Mode Optimisation</a></li>
              <li><a href="/services/preferred-sources-strategy/" className="text-white/50 hover:text-white text-sm transition-colors">Preferred Sources Strategy</a></li>
              <li><a href="/services/ai-citation-service/" className="text-white/50 hover:text-white text-sm transition-colors">AI Citation Programme</a></li>
              <li><a href="/services/ai-websites/" className="text-white/50 hover:text-white text-sm transition-colors">AI Websites &amp; Schema</a></li>
              <li><a href="/services/account-based-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Account-Based Marketing</a></li>
              <li><a href="/services/ai-marketing-strategy/" className="text-white/50 hover:text-white text-sm transition-colors">AI Growth Strategy</a></li>
              <li><a href="/services/integrated-marketing-communications/" className="text-white/50 hover:text-white text-sm transition-colors">Integrated Marketing</a></li>
              <li><a href="/services/ai-governance/" className="text-white/50 hover:text-white text-sm transition-colors">AI Governance</a></li>
              <li className="pt-1 border-t border-white/10 mt-1"></li>
              {!profileRestrictedServices && (
                <>
                  <li><a href="/services/ai-marketing-agency-london/" className="text-white/40 hover:text-white/70 text-xs transition-colors">AI marketing agency London</a></li>
                  <li><a href="/services/aeo-agency-london/" className="text-white/40 hover:text-white/70 text-xs transition-colors">AEO agency London</a></li>
                </>
              )}
              <li><a href="/services/" className="text-white/40 hover:text-white/70 text-xs transition-colors">All services →</a></li>
            </ul>
          </FooterAccordion>

          {/* Resources */}
          <FooterAccordion title="Resources" titleColor="#00D4AA">
            <ul className="space-y-2">
              <li><a href="/aeo/" className="text-[#00D4AA]/70 hover:text-[#00D4AA] text-sm transition-colors font-medium">AEO Content Hub</a></li>
              <li><a href="/case-studies/" className="text-white/50 hover:text-white text-sm transition-colors">Case Studies</a></li>
              <li><a href="/blog/" className="text-white/50 hover:text-white text-sm transition-colors">Blog</a></li>
              <li><a href="/ai-marketing-news/" className="text-white/50 hover:text-white text-sm transition-colors">AI Marketing News</a></li>
              <li><a href="/tools/" className="text-white/50 hover:text-white text-sm transition-colors">Free AI Marketing Tools</a></li>
              <li><a href="/methodology/" className="text-white/50 hover:text-white text-sm transition-colors">Methodology</a></li>
              <li><a href="/provider-scope-card/" className="text-white/50 hover:text-white text-sm transition-colors">Provider Scope Card</a></li>
              <li><a href="/resources/ai-tools/" className="text-white/50 hover:text-white text-sm transition-colors">AI Tools Hub</a></li>
              <li><a href="/faq/" className="text-white/50 hover:text-white text-sm transition-colors">FAQs</a></li>
              <li><a href="/modi-elnadi/" className="text-white/50 hover:text-white text-sm transition-colors">About Modi Elnadi</a></li>
            </ul>
          </FooterAccordion>

          {/* Local business guides: industry pages, not office-location claims. */}
          <FooterAccordion title="Local business guides" titleColor="#FFB25F">
            <ul className="space-y-2">
              <li><a href="/local/" className="text-[#FFB25F]/90 hover:text-[#FFB25F] text-sm font-medium transition-colors">Local business marketing</a></li>
              <li><a href="/local/dentist-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Dental practices</a></li>
              <li><a href="/local/cafe-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Cafés and restaurants</a></li>
              <li><a href="/local/beauty-salon-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Beauty and tanning</a></li>
              <li><a href="/local/dog-grooming-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Pet businesses</a></li>
              <li><a href="/local/gym-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Gyms and studios</a></li>
              <li><a href="/local/solicitor-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Solicitors</a></li>
              <li><a href="/local/garage-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Garages and MOT</a></li>
              <li><a href="/local/driving-instructor-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Driving instructors</a></li>
              <li><a href="/local/tutor-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Tutors</a></li>
              <li><a href="/local/window-cleaner-marketing/" className="text-white/50 hover:text-white text-sm transition-colors">Window cleaners</a></li>
            </ul>
          </FooterAccordion>

          {/* Contact */}
          <FooterAccordion title="Contact" titleColor="#FF8C00">
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-white/50 text-sm">
                <Mail size={14} className="text-[#FF8C00]" />
                <a aria-label="Email Integrated.Social at info@integrated.social" href="mailto:info@integrated.social" className="hover:text-white transition-colors" onClick={() => trackEmailClick({ email_address: "info@integrated.social", link_location: "footer" })}>
                  info@integrated.social
                </a>
              </li>
              <li className="flex items-center gap-2 text-white/50 text-sm">
                <Phone size={14} className="text-[#FF8C00]" />
                <a href="tel:+447717804077" className="hover:text-white transition-colors">+44 7717 804 077</a>
              </li>
              <li className="flex items-center gap-2 text-white/50 text-sm">
                <MessageCircle size={14} className="text-[#25D366]" />
                <a href="https://wa.me/447717804077" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" onClick={() => trackOutboundSocialClick({ social_platform: "whatsapp", link_location: "footer_contact", destination_url: "https://wa.me/447717804077" })}>WhatsApp Us</a>
              </li>
              <li className="text-white/50 text-sm mt-4">
                Integrated Social Ltd.<br />
                London, SW15 4LE<br />
                United Kingdom
              </li>
            </ul>
          </FooterAccordion>
        </div>

        {/* Footer CTA */}
        <div className="mb-10 rounded-xl border border-[#FF8C00]/30 bg-gradient-to-r from-[#FF8C00]/10 via-[#0F1419] to-[#00D4AA]/10 px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-serif text-lg leading-snug mb-1">
              Is your brand <span className="text-[#FF8C00] italic">easy to find</span> in relevant AI answers?
            </p>
            <p className="text-white/50 text-sm">Free AI Visibility Score. No credit card. Results are sent after email verification.</p>
          </div>
          <a
            href="/free-ai-growth-audit/"
            className="footer-cta-btn shrink-0 inline-flex items-center gap-2 bg-[#FF8C00] hover:bg-[#e07800] hover:scale-105 hover:shadow-[0_0_20px_rgba(255,140,0,0.5)] active:scale-[0.97] text-white font-semibold text-sm px-6 py-3 rounded-lg transition-all duration-200"
          >
            Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </a>
        </div>

        {/* AI Platform Logo Strip */}
        <div className="mb-8">
          <p className="text-white/50 text-xs uppercase tracking-widest font-medium text-center mb-4">Built to support visibility in</p>
          <div className="grid grid-cols-4 sm:flex sm:flex-wrap sm:justify-center gap-3 sm:gap-5">
            {([
              {
                src: "/media/platform-chatgpt_b1bca021.png",
                alt: "ChatGPT",
                label: "ChatGPT",
                tooltip: "We structure your content so ChatGPT can read and understand your brand. We cannot control what it cites.",
                href: "/services/seo-aeo-geo/",
              },
              {
                src: "/media/platform-claude_391f6950.png",
                alt: "Claude",
                label: "Claude",
                tooltip: "We build clear entity information and structured data so Claude has accurate facts about your brand.",
                href: "/services/preferred-sources-strategy/",
              },
              {
                src: "/media/platform-gemini_e09e44be.png",
                alt: "Google Gemini",
                label: "Gemini",
                tooltip: "We improve schema, evidence and page structure for Google Search and its AI answer experiences.",
                href: "/services/google-ai-mode-optimisation/",
              },
              {
                src: "/media/platform-perplexity_8badde0d.png",
                alt: "Perplexity",
                label: "Perplexity",
                tooltip: "We write long-form content, structured FAQs and GEO signals that help Perplexity find and use your pages. We do not promise citations.",
                href: "/services/seo-aeo-geo/",
              },
              {
                src: "/media/platform-bing_a560fc66.png",
                alt: "Microsoft Copilot",
                label: "Copilot",
                tooltip: "We align content and technical foundations with Bing indexing and clear commercial intent.",
                href: "/services/seo-aeo-geo/",
              },
              {
                src: "/media/platform-google_57f62604.png",
                alt: "Google Search",
                label: "Google",
                tooltip: "We improve technical SEO, Core Web Vitals and AEO-ready structured data.",
                href: "/services/seo-aeo-geo/",
              },
              {
                src: "/media/platform-grok_b5f9a95d.png",
                alt: "Grok xAI",
                label: "Grok",
                tooltip: "We build real-time brand presence on X and structured content Grok can read.",
                href: "/blog/",
              },
              {
                src: "/media/platform-manus_077c6bb9.png",
                alt: "Manus AI",
                label: "Manus",
                tooltip: "We use Manus agentic workflows to support research and lead-generation tasks, with a person checking the output.",
                href: "/services/gemini-agentic-ai/",
              },
            ] as { src: string; alt: string; label: string; tooltip: string; href: string }[]).map(({ src, alt, label, tooltip, href }, index) => (
              <a
                key={alt}
                href={href}
                className="group relative flex flex-col items-center gap-1 opacity-55 hover:opacity-95 transition-opacity duration-200 cursor-pointer"
                aria-label={`${alt} - ${tooltip}`}
              >
                <img
                  src={src}
                  alt={alt}
                  className="h-7 w-7 object-contain"
                  loading="lazy"
                  decoding="async"
                  width={28}
                  height={28}
                />
                <span className="text-white/40 text-[9px] uppercase tracking-wide">{label}</span>
                {/* Tooltip */}
                <span
                  className={`pointer-events-none absolute bottom-full mb-2 w-52 rounded-lg bg-[#1a2030] border border-white/10 px-3 py-2 text-[11px] text-white/80 leading-relaxed shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 text-center ${index % 4 === 0 ? "left-0 translate-x-0" : index % 4 === 3 ? "right-0 translate-x-0" : "left-1/2 -translate-x-1/2"}`}
                  role="tooltip"
                >
                  {tooltip}
                  <span className={`absolute top-full border-4 border-transparent border-t-[#1a2030] ${index % 4 === 0 ? "left-3 -translate-x-1/2" : index % 4 === 3 ? "right-3 translate-x-1/2" : "left-1/2 -translate-x-1/2"}`} />
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-xs">
            &copy; {new Date().getFullYear()} Integrated Social Ltd. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-white/30 text-xs">
            <a href="/privacy-policy/" className="hover:text-white/60 transition-colors">Privacy Policy</a>
            <span>|</span>
            <a href="/cookie-policy/" className="hover:text-white/60 transition-colors">Cookie Policy</a>
            <span>|</span>
            <button
              onClick={() => (window as any).__openCookieConsent?.()}
              className="hover:text-white/60 transition-colors cursor-pointer bg-transparent border-0 p-0 text-white/30 text-xs"
            >
              Cookie Settings
            </button>
            <span>|</span>
            <a href="/editorial-policy/" className="hover:text-white/60 transition-colors">Editorial Policy</a>
            <span>|</span>
            <a href="/methodology/" className="hover:text-white/60 transition-colors">Methodology</a>
            <span>|</span>
            <a href="/rss.xml" className="hover:text-white/60 transition-colors">RSS Feed</a>
          </div>
          <p className="text-white/30 text-xs">
            <a href="/services/gemini-agentic-ai/" className="hover:text-white/60 transition-colors">Agentic AI</a>
            {" · "}
            <a href="/services/seo-aeo-geo/" className="hover:text-white/60 transition-colors">AEO</a>
            {" · "}
            <a href="/services/seo-aeo-geo/" className="hover:text-white/60 transition-colors">GEO</a>
            {" · "}
            <a href="/services/seo-aeo-geo/" className="hover:text-white/60 transition-colors">LLMO</a>
            {" · "}
            <a href="/services/ppc-performance-max/" className="hover:text-white/60 transition-colors">Performance Max</a>
            {" · "}
            <a href="/services/google-ai-mode-optimisation/" className="hover:text-white/60 transition-colors">AI Mode</a>
            {" · "}
            <span>London</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
